<template>
    <v-container fluid class="pa-8 bg-grey-lighten-5 h-100">
        <div class="d-flex justify-space-between align-center mb-6">
            <div>
                <h2 class="text-h4 font-weight-bold text-grey-darken-3">테이블 관리</h2>
                <p class="text-subtitle-1 text-grey-darken-1 mt-1">시스템에 등록된 데이터베이스 테이블을 관리합니다.</p>
            </div>
        </div>

        <v-card class="rounded-xl border-none shadow-soft" elevation="0" color="white">
            <div class="pa-6 d-flex justify-space-between align-center">
                <div class="d-flex align-center">
                    <v-avatar color="indigo-lighten-5" class="mr-4" size="48">
                        <v-icon color="indigo-darken-2" size="24">mdi-database</v-icon>
                    </v-avatar>
                    <div>
                        <h3 class="text-h6 font-weight-bold text-grey-darken-3">테이블 목록</h3>
                        <span class="text-caption text-grey-darken-1">총 {{ tables.length }}개의 테이블이 있습니다</span>
                    </div>
                </div>
                <div class="d-flex align-center">
                    <v-select v-model="systemFilter" :items="systemOptions" label="시스템 선택" variant="outlined"
                        density="compact" hide-details class="mr-4" style="width: 180px;"
                        @update:model-value="fetchTables"></v-select>
                    <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="테이블 검색" single-line
                        hide-details variant="outlined" density="compact" class="mr-4 search-field"
                        style="width: 280px; border-radius: 12px;" bg-color="grey-lighten-5" color="indigo"
                        @click:prepend-inner="fetchTables" @keyup.enter="fetchTables"></v-text-field>
                    <v-btn color="indigo-darken-2" prepend-icon="mdi-database-import" @click="openImportDialog"
                        elevation="2" height="40" class="px-6 text-capitalize rounded-lg font-weight-bold">
                        테이블 가져오기
                    </v-btn>
                </div>
            </div>

            <v-divider class="border-opacity-50"></v-divider>

            <v-data-table :headers="headers" :items="tables" :search="search" :loading="loading"
                class="pa-2 modern-table" hover :items-per-page="10">
                <template v-slot:[`item.catId`]="{ item }">
                    <v-chip size="small" variant="tonal" color="indigo" class="font-weight-medium">
                        {{ getCategoryName(item.raw.catId) }}
                    </v-chip>
                </template>
                <template v-slot:[`item.sysId`]="{ item }">
                    <v-chip size="small" variant="flat"
                        :color="getSystemColor(item.raw.sysId)"
                        class="font-weight-bold">
                        {{ getSystemName(item.raw.sysId) }}
                    </v-chip>
                </template>
                <template v-slot:[tableNameSlot]="{ item }">
                    <div class="d-flex align-center py-3">
                        <v-avatar size="32" color="indigo-lighten-5" class="mr-3">
                            <v-icon size="18" color="indigo-lighten-1">mdi-table</v-icon>
                        </v-avatar>
                        <a href="#" @click.prevent="openViewDialog(item.raw)"
                            class="text-decoration-none font-weight-bold text-indigo-darken-2 table-link text-body-1">
                            {{ item.raw.tableName }}
                        </a>
                    </div>
                </template>
                <template v-slot:[tableDescSlot]="{ item }">
                    <div class="text-truncate text-grey-darken-1" style="max-width: 300px;">
                        {{ item.raw.tableDesc || '-' }}
                    </div>
                </template>
                <template v-slot:[schemaSlot]="{ item }">
                    <div class="d-flex align-center">
                        <v-chip size="small" color="blue-grey-lighten-4" variant="flat"
                            class="mr-2 text-blue-grey-darken-3 font-weight-bold font-monospace">SCHEMA</v-chip>
                        <div class="text-truncate text-grey-darken-2 font-monospace"
                            style="max-width: 350px; font-size: 0.85em;">
                            {{ item.raw.schemaDef }}
                        </div>
                    </div>
                </template>
                <template v-slot:[addedTimeSlot]="{ item }">
                    <div class="text-body-2 text-grey-darken-1">
                        {{ formatDate(item.raw.addedTime) }}
                    </div>
                </template>
            </v-data-table>
        </v-card>

        <!-- Import Dialog -->
        <v-dialog v-model="importDialog" width="80%" persistent>
            <v-card class="rounded-xl shadow-soft h-100" style="height: 80vh; min-height: 700px;">
                <v-card-title class="pa-5 d-flex justify-space-between align-center border-bottom bg-grey-lighten-5">
                    <div>
                        <span class="text-h5 font-weight-bold text-grey-darken-3">테이블 가져오기</span>
                        <div class="text-subtitle-2 text-grey-darken-1 mt-1">데이터베이스의 최신 테이블 정보를 불러옵니다.</div>
                    </div>
                    <div class="d-flex align-center">
                        <v-select v-model="importSystem" :items="importSystemOptions" label="시스템 선택" variant="outlined"
                            density="compact" hide-details class="mr-4" style="width: 180px;"
                            @update:model-value="fetchImportTables"></v-select>
                        <v-btn icon="mdi-close" variant="text" color="grey" @click="closeImportDialog"
                            density="comfortable"></v-btn>
                    </div>
                </v-card-title>

                <v-divider></v-divider>

                <v-card-text class="pa-0">
                    <v-data-table :headers="importHeaders" :items="importTables" :loading="importLoading"
                        v-model="selectedImportTables" show-select item-value="tableName" density="compact"
                        class="virtual-scroll-table" fixed-header height="500px" hover
                        @click:row="(e, { item }) => openImportPreviewDialog(item)">
                        <template v-slot:[statusSlot]="{ item }">
                            <v-chip :color="item.raw.status === 'NEW' ? 'green-lighten-4' : 'amber-lighten-4'"
                                :text-color="item.raw.status === 'NEW' ? 'green-darken-3' : 'amber-darken-3'"
                                size="small" class="font-weight-bold" variant="flat">
                                {{ item.raw.status === 'NEW' ? '신규' : '업데이트 가능' }}
                            </v-chip>
                        </template>
                        <template v-slot:[tableDescSlot]="{ item }">
                            <div class="text-truncate text-grey-darken-2" style="max-width: 250px; font-size: 0.85em;">
                                {{ item.raw.introspectTableDesc }}
                            </div>
                        </template>
                        <template v-slot:[importSchemaSlot]="{ item }">
                            <div class="text-truncate text-grey-darken-1 font-monospace"
                                style="max-width: 200px; font-size: 0.8em;">
                                {{ item.raw.schemaDef }}
                            </div>
                        </template>
                        <template v-slot:[actionSlot]="{ item }">
                            <v-btn v-if="item.raw.status === 'EXISTS'" size="small" variant="tonal" color="indigo"
                                @click.stop="openCompareDialog(item.raw)">
                                비교
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-5 bg-grey-lighten-5">
                    <div class="text-caption text-grey-darken-1 ml-2">
                        {{ selectedImportTables.length }}개 선택됨
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn variant="outlined" color="grey-darken-1" @click="closeImportDialog"
                        class="px-6 rounded-lg mr-2" height="40">
                        취소
                    </v-btn>
                    <v-btn color="indigo-darken-2" @click="saveSelectedTables" class="px-6 rounded-lg" elevation="2"
                        height="40" :loading="saveLoading" :disabled="selectedImportTables.length === 0">
                        선택항목 저장
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Compare Dialog -->
        <v-dialog v-model="compareDialog" width="80%" scrollable>
            <v-card class="rounded-xl shadow-soft" style="height: 80vh; min-height: 700px;">
                <v-card-title class="pa-0 bg-indigo-darken-2 text-white">
                    <div class="d-flex justify-space-between align-center px-4 py-3">
                        <div>
                            <span class="text-h6 font-weight-bold">스키마 변경사항 비교</span>
                            <div class="text-caption text-indigo-lighten-3">{{ compareTable.tableName }}</div>
                        </div>
                        <v-btn icon="mdi-close" variant="text" color="white" @click="compareDialog = false"
                            density="comfortable"></v-btn>
                    </div>
                </v-card-title>

                <div class="d-flex flex-column flex-grow-1 overflow-hidden bg-white">
                    <!-- Toolbar -->
                    <div class="d-flex align-center px-4 py-1 border-bottom bg-grey-lighten-5">
                        <v-chip size="small" :color="hasChanges ? 'red-lighten-4' : 'green-lighten-4'"
                            class="mr-3 font-weight-bold"
                            :class="hasChanges ? 'text-red-darken-3' : 'text-green-darken-3'">
                            {{ hasChanges ? '변경사항 있음' : '동일함' }}
                        </v-chip>
                        <v-spacer></v-spacer>
                        <v-btn :prepend-icon="isCompareEditing ? 'mdi-check' : 'mdi-pencil'"
                            :color="isCompareEditing ? 'green-darken-1' : 'grey-darken-3'" variant="tonal" size="small"
                            class="mr-2" @click="toggleCompareEdit">
                            {{ isCompareEditing ? '수정 완료' : '기존 내용 수정' }}
                        </v-btn>
                    </div>

                    <!-- Header -->
                    <div class="d-flex text-caption font-weight-bold text-center bg-grey-lighten-4 border-bottom">
                        <div
                            class="flex-grow-1 pa-1 border-right text-grey-darken-3 w-50 d-flex align-center justify-center">
                            <v-icon size="small" class="mr-1">mdi-server</v-icon>
                            현재 (기존)
                        </div>
                        <div class="flex-grow-1 pa-1 text-indigo-darken-3 w-50 d-flex align-center justify-center">
                            <v-icon size="small" class="mr-1">mdi-database-import</v-icon>
                            새로 불러옴 (변경)
                        </div>
                    </div>

                    <!-- Diff Content -->
                    <v-card-text class="pa-0 font-monospace text-body-2 custom-scroll d-flex flex-grow-1"
                        style="height: 600px; overflow-y: auto; background-color: #fff;">
                        <!-- Left Pane (Editable or View) -->
                        <div class="w-50 border-right d-flex flex-column" style="min-height: 100%;">
                            <div class="flex-grow-1 d-flex flex-column">
                                <template v-if="isCompareEditing">
                                    <v-textarea v-model="compareTable.originalSchema" variant="plain"
                                        class="flex-grow-1 pa-0 font-monospace" hide-details no-resize
                                        spellcheck="false" style="line-height: 1.5;"
                                        :rows="diffLines.length || 20"></v-textarea>
                                </template>
                                <template v-else>
                                    <div v-for="(line, index) in diffLines" :key="'left-' + index"
                                        class="d-flex align-stretch px-2 line-row" :class="getDiffClass(line, 'left')"
                                        style="min-height: 24px;">
                                        <div class="text-grey-lighten-1 mr-3 select-none text-right"
                                            style="width: 30px; font-size: 0.85em;">
                                            {{ line.leftNum || '' }}
                                        </div>
                                        <div class="flex-grow-1 code-text"
                                            style="white-space: pre-wrap; word-break: break-all;">{{ line.leftContent }}
                                        </div>
                                    </div>
                                </template>
                            </div>

                            <!-- Description Area (Left Only) -->
                            <div class="ma-3 pa-3 bg-grey-lighten-5 rounded border">
                                <div class="text-caption font-weight-bold text-grey-darken-2 mb-1">상세 설명</div>
                                <v-textarea v-if="isCompareEditing" v-model="compareTable.originalTableDesc"
                                    variant="outlined" density="compact" rows="3" hide-details bg-color="white"
                                    class="text-body-2" placeholder="테이블 상세 설명을 입력하세요"></v-textarea>
                                <div v-else class="text-body-2 text-grey-darken-3" style="white-space: pre-wrap;">
                                    {{ compareTable.originalTableDesc || '(설명 없음)' }}
                                </div>
                            </div>
                        </div>

                        <!-- Right Pane (Read-only) -->
                        <div class="w-50 d-flex flex-column" style="min-height: 100%; background-color: #fafafa;">
                            <div class="flex-grow-1">
                                <div v-for="(line, index) in diffLines" :key="'right-' + index"
                                    class="d-flex align-stretch px-2 line-row" :class="getDiffClass(line, 'right')"
                                    style="min-height: 24px;">
                                    <div class="text-grey-lighten-1 mr-3 select-none text-right"
                                        style="width: 30px; font-size: 0.85em;">
                                        {{ line.rightNum || '' }}
                                    </div>
                                    <div class="flex-grow-1 code-text"
                                        style="white-space: pre-wrap; word-break: break-all;">{{
                                            line.rightContent }}</div>
                                </div>
                            </div>
                        </div>
                    </v-card-text>
                </div>

                <v-divider></v-divider>

                <v-card-actions class="pa-2 bg-white">
                    <v-btn v-if="!isCompareEditing" color="green-darken-1" variant="text" @click="saveCurrentSchema"
                        prepend-icon="mdi-content-save" class="ml-2" :loading="saveLoading">
                        현재 내용(좌측) 저장
                    </v-btn>

                    <v-spacer></v-spacer>
                    <v-btn color="indigo-darken-2" variant="flat" @click="confirmUpdateFromCompare"
                        prepend-icon="mdi-check-circle" class="px-6">
                        이 버전으로 선택
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- View Dialog -->
        <!-- View Dialog -->
        <v-dialog v-model="viewDialog" width="80%" scrollable>
            <v-card class="rounded-xl overflow-hidden shadow-soft" style="height: 80vh; min-height: 700px;">
                <div class="bg-indigo-darken-2 pa-6 text-white">
                    <div class="d-flex justify-space-between align-start">
                        <div>
                            <div class="text-overline text-indigo-lighten-3 mb-1">TABLE DETAILS</div>
                            <h3 class="text-h4 font-weight-bold" v-if="!isEditing">{{ selectedTable.tableName }}</h3>
                            <div v-else class="d-flex align-center mt-2">
                                <v-text-field v-model="editTable.tableName" variant="outlined" density="compact"
                                    bg-color="indigo-darken-3" color="white" hide-details class="text-white"
                                    style="min-width: 300px;"></v-text-field>
                            </div>
                        </div>
                        <div class="d-flex">
                            <v-btn v-if="!isEditing" icon="mdi-pencil" variant="text" color="indigo-lighten-3"
                                @click="startEdit" density="comfortable" class="mr-2"></v-btn>
                            <v-btn v-if="!isEditing" icon="mdi-delete" variant="text" color="red-lighten-3"
                                @click="deleteTable" density="comfortable" class="mr-2"></v-btn>
                            <v-btn icon="mdi-close" variant="text" color="white" @click="viewDialog = false"
                                density="comfortable"></v-btn>
                        </div>
                    </div>
                    <div class="d-flex align-center mt-4 text-indigo-lighten-4">
                        <v-icon size="small" class="mr-2">mdi-clock-outline</v-icon>
                        <span>생성일: {{ formatDate(selectedTable.addedTime) }}</span>
                    </div>
                </div>

                <v-card-text class="pa-6 bg-grey-lighten-5">
                    <div v-if="!isEditing">
                        <!-- Detail Description View -->
                        <v-sheet v-if="selectedTable.catId" class="rounded-lg border pa-4 mb-4 bg-white" elevation="0">
                            <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2">분류</div>
                            <div class="text-body-2 text-indigo-darken-2 font-weight-bold">{{
                                getFullCategoryPath(selectedTable.catId) }}</div>
                        </v-sheet>

                        <v-sheet v-if="selectedTable.tableDesc" class="rounded-lg border pa-4 mb-4 bg-white"
                            elevation="0">
                            <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2">상세 설명</div>
                            <div class="text-body-2 text-grey-darken-3">{{ selectedTable.tableDesc }}</div>
                        </v-sheet>

                        <v-sheet class="rounded-lg border pa-0 overflow-hidden" elevation="0">
                            <div class="pa-3 bg-grey-lighten-4 border-bottom d-flex align-center">
                                <v-icon size="small" class="mr-2 text-grey-darken-2">mdi-code-tags</v-icon>
                                <span class="text-subtitle-2 font-weight-bold text-grey-darken-3">Schema
                                    Definition</span>
                            </div>
                            <div class="pa-4 bg-grey-darken-4">
                                <pre class="text-body-2 text-shades-white font-weight-regular"
                                    style="font-family: 'Fira Code', monospace; line-height: 1.6;">{{
                                        formatSchema(selectedTable.schemaDef) }}</pre>
                            </div>
                        </v-sheet>
                    </div>
                    <div v-else>
                        <!-- Category Selection Edit -->
                        <v-sheet class="rounded-lg border pa-4 mb-4 bg-white" elevation="0">
                            <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-3">분류 선택</div>
                            <v-row dense>
                                <v-col cols="4">
                                    <v-select v-model="selectedL1" :items="l1List" item-title="catNm" item-value="catId"
                                        label="대분류" variant="outlined" density="compact" hide-details
                                        @update:model-value="selectedL2 = null; selectedL3 = null"></v-select>
                                </v-col>
                                <v-col cols="4">
                                    <v-select v-model="selectedL2" :items="l2List" item-title="catNm" item-value="catId"
                                        label="중분류" variant="outlined" density="compact" hide-details
                                        :disabled="!selectedL1" @update:model-value="selectedL3 = null"></v-select>
                                </v-col>
                                <v-col cols="4">
                                    <v-select v-model="selectedL3" :items="l3List" item-title="catNm" item-value="catId"
                                        label="소분류" variant="outlined" density="compact" hide-details
                                        :disabled="!selectedL2"></v-select>
                                </v-col>
                            </v-row>
                        </v-sheet>

                        <!-- Detail Description Edit -->
                        <v-sheet class="rounded-lg border pa-4 mb-4 bg-white" elevation="0">
                            <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2">상세 설명 (Editable)</div>
                            <v-textarea v-model="editTable.tableDesc" variant="outlined" rows="3" color="indigo"
                                hide-details="auto" class="text-body-2"></v-textarea>
                        </v-sheet>

                        <v-sheet class="rounded-lg border pa-0 overflow-hidden" elevation="0">
                            <div class="pa-3 bg-grey-lighten-4 border-bottom d-flex align-center">
                                <v-icon size="small" class="mr-2 text-grey-darken-2">mdi-code-tags</v-icon>
                                <span class="text-subtitle-2 font-weight-bold text-grey-darken-3">Schema Definition
                                    (Editable)</span>
                            </div>
                            <div class="pa-4 bg-white">
                                <v-textarea v-model="editTable.schemaDef" variant="outlined" rows="10" color="indigo"
                                    hide-details="auto" class="font-monospace rounded-lg"></v-textarea>
                            </div>
                        </v-sheet>
                    </div>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4 bg-white">
                    <v-spacer></v-spacer>
                    <template v-if="!isEditing">
                        <v-btn color="grey-darken-1" variant="text" @click="viewDialog = false" class="px-6">
                            닫기
                        </v-btn>
                    </template>
                    <template v-else>
                        <v-btn color="grey-darken-1" variant="outlined" @click="cancelEdit"
                            class="px-6 mr-2 rounded-lg">
                            취소
                        </v-btn>
                        <v-btn color="indigo-darken-2" variant="flat" @click="saveEdit" class="px-6 rounded-lg">
                            저장
                        </v-btn>
                    </template>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Import Preview Dialog -->
        <v-dialog v-model="importPreviewDialog" width="60%" scrollable>
            <v-card class="rounded-xl overflow-hidden shadow-soft" style="height: 70vh; min-height: 500px;">
                <div class="bg-indigo-darken-2 pa-5 text-white">
                    <div class="d-flex justify-space-between align-center">
                        <div>
                            <div class="text-overline text-indigo-lighten-3 mb-1">TABLE IMPORT PREVIEW</div>
                            <h3 class="text-h5 font-weight-bold">{{ selectedImportPreview.tableName }}</h3>
                        </div>
                        <v-btn icon="mdi-close" variant="text" color="white" @click="importPreviewDialog = false"
                            density="comfortable"></v-btn>
                    </div>
                </div>

                <v-card-text class="pa-6 bg-grey-lighten-5">
                    <v-sheet v-if="selectedImportPreview.introspectTableDesc"
                        class="rounded-lg border pa-4 mb-4 bg-white" elevation="0">
                        <div class="text-subtitle-2 font-weight-bold text-grey-darken-2 mb-2">상세 설명 (Comment)</div>
                        <div class="text-body-2 text-grey-darken-3" style="white-space: pre-wrap;">{{
                            selectedImportPreview.introspectTableDesc }}</div>
                    </v-sheet>

                    <v-sheet class="rounded-lg border pa-0 overflow-hidden" elevation="0">
                        <div class="pa-3 bg-grey-lighten-4 border-bottom d-flex align-center">
                            <v-icon size="small" class="mr-2 text-grey-darken-2">mdi-code-tags</v-icon>
                            <span class="text-subtitle-2 font-weight-bold text-grey-darken-3">Detected Schema</span>
                        </div>
                        <div class="pa-4 bg-grey-darken-4">
                            <pre class="text-body-2 text-shades-white font-weight-regular"
                                style="font-family: 'Fira Code', monospace; line-height: 1.6; overflow-x: auto;">{{
                                    formatSchema(selectedImportPreview.schemaDef) }}</pre>
                        </div>
                    </v-sheet>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions class="pa-4 bg-white">
                    <v-spacer></v-spacer>
                    <v-btn color="grey-darken-1" variant="text" @click="importPreviewDialog = false" class="px-6">
                        닫기
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import { computed, ref, onMounted, reactive, getCurrentInstance } from 'vue';

const { proxy } = getCurrentInstance();
const axios = proxy.$axios;

// Dialogs
const importDialog = ref(false);
const viewDialog = ref(false);
const compareDialog = ref(false);
const importPreviewDialog = ref(false);

// Loading states
const loading = ref(false);
const importLoading = ref(false);
const saveLoading = ref(false);

// Search
const search = ref('');

// Data
const tables = ref([]);
const importTables = ref([]);
const selectedImportTables = ref([]);
const selectedImportPreview = ref({});

// Slot name constants
const addedTimeSlot = 'item.addedTime';
const schemaSlot = 'item.schemaDef';
const tableNameSlot = 'item.tableName';
const statusSlot = 'item.status';
const importSchemaSlot = 'item.schemaDef';
const actionSlot = 'item.action';
const tableDescSlot = 'item.tableDesc';

const systemFilter = ref('all');
const importSystem = ref('');
const systemOptions = ref([
    { title: '전체', value: 'all' }
]);

const importSystemOptions = computed(() => {
    return systemOptions.value.filter(opt => opt.value !== 'all');
});

const selectedTable = ref({});
const isEditing = ref(false);
const editTable = reactive({
    tableName: '',
    schemaDef: '',
    tableDesc: '',
    catId: null
});

const categories = ref([]);
const selectedL1 = ref(null);
const selectedL2 = ref(null);
const selectedL3 = ref(null);

const l1List = computed(() => categories.value.filter(c => c.catLvl === 1));
const l2List = computed(() => categories.value.filter(c => c.catLvl === 2 && c.upperCatId === selectedL1.value));
const l3List = computed(() => categories.value.filter(c => c.catLvl === 3 && c.upperCatId === selectedL2.value));

const fetchCategories = async () => {
    try {
        const res = await axios.post('/rag/category/list', {});
        categories.value = res.data.data.list;
    } catch (err) {
        console.error('Failed to fetch categories:', err);
    }
};

const fetchSystemInfo = async () => {
    try {
        const res = await axios.post('/common/sys-info', {});
        if (res.data.status === 'success') {
            const list = res.data.data.list;
            // systemOptions 업데이트
            systemOptions.value = [
                { title: '전체', value: 'all' },
                ...list.map(s => ({ title: s.sysNm, value: s.sysId }))
            ];
            // importSystem 초기값 설정
            if (list.length > 0) {
                importSystem.value = list[0].sysId;
            }
        }
    } catch (err) {
        console.error('Failed to fetch system info:', err);
    }
};

const getCategoryName = (catId) => {
    if (!catId) return '-';
    const cat = categories.value.find(c => String(c.catId) === String(catId));
    return cat ? cat.catNm : '-';
};

const getFullCategoryPath = (catId) => {
    if (!catId) return '-';
    const path = [];
    let currentCat = categories.value.find(c => String(c.catId) === String(catId));
    while (currentCat) {
        path.unshift(currentCat.catNm);
        currentCat = categories.value.find(c => c.catId === currentCat.upperCatId);
    }
    return path.join(' > ');
};

const getSystemName = (sysId) => {
    const sys = systemOptions.value.find(s => s.value === sysId);
    return sys ? sys.title : sysId;
};

const systemColors = ['blue-lighten-4', 'cyan-lighten-4', 'deep-purple-lighten-4', 'orange-lighten-4', 'teal-lighten-4'];

const getSystemColor = (sysId) => {
    const index = systemOptions.value.findIndex(system => system.value === sysId);
    return index > 0 ? systemColors[(index - 1) % systemColors.length] : 'grey-lighten-4';
};

// Compare Feature
const compareTable = ref({});
const diffLines = ref([]);
const isCompareEditing = ref(false);

const hasChanges = computed(() => {
    return diffLines.value.some(line => line.leftType !== 'same' || line.rightType !== 'same');
});

const headers = [
    { title: '시스템', key: 'sysId', align: 'start', width: '10%', sortable: true },
    { title: '테이블명', key: 'tableName', align: 'start', width: '20%', sortable: true },
    { title: '분류', key: 'catId', align: 'start', width: '15%', sortable: true },
    { title: '상세 설명', key: 'tableDesc', align: 'start', width: '20%', sortable: false },
    { title: '스키마', key: 'schemaDef', align: 'start', width: '20%', sortable: false },
    { title: '추가 시간', key: 'addedTime', align: 'end', width: '15%', sortable: true },
];

const importHeaders = [
    { title: '상태', key: 'status', align: 'center', width: '10%', sortable: true },
    { title: '테이블명', key: 'tableName', align: 'start', width: '20%', sortable: true },
    { title: '상세 설명', key: 'tableDesc', align: 'start', width: '25%', sortable: false },
    { title: '감지된 스키마', key: 'schemaDef', align: 'start', width: '30%', sortable: false },
    { title: '작업', key: 'action', align: 'center', width: '15%', sortable: false },
];

const openImportDialog = () => {
    importDialog.value = true;
    selectedImportTables.value = [];
    fetchImportTables();
};

const closeImportDialog = () => {
    importDialog.value = false;
};

const openImportPreviewDialog = (item) => {
    selectedImportPreview.value = item.raw;
    importPreviewDialog.value = true;
};

const openViewDialog = (item) => {
    // Basic info from list
    selectedTable.value = { ...item };
    isEditing.value = false;

    // Fetch full details
    loading.value = true;
    axios.post('/rag/database/table/detail', { tableName: item.tableName }, {
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => {
            if (res.data.status === 'success') {
                const detailInfo = res.data.data.info;
                // Update with full schema
                if (detailInfo) {
                    selectedTable.value.schemaDef = detailInfo.schemaDef;
                    selectedTable.value.tableDesc = detailInfo.tableDesc;
                }
                viewDialog.value = true;
            } else {
                alert('테이블 상세 정보를 불러오는데 실패했습니다.');
            }
        })
        .catch(err => {
            console.error(err);
            alert('테이블 상세 정보를 불러오는 중 오류가 발생했습니다.');
        })
        .finally(() => {
            loading.value = false;
        });
};

const startEdit = () => {
    editTable.tableName = selectedTable.value.tableName;
    editTable.schemaDef = selectedTable.value.schemaDef;
    editTable.tableDesc = selectedTable.value.tableDesc || '';
    editTable.catId = selectedTable.value.catId;

    // Set hierarchical selection
    if (editTable.catId) {
        const cat = categories.value.find(c => String(c.catId) === String(editTable.catId));
        if (cat) {
            if (cat.catLvl === 3) {
                selectedL3.value = cat.catId;
                selectedL2.value = cat.upperCatId;
                const parentCat = categories.value.find(c => c.catId === cat.upperCatId);
                if (parentCat) selectedL1.value = parentCat.upperCatId;
            } else if (cat.catLvl === 2) {
                selectedL2.value = cat.catId;
                selectedL1.value = cat.upperCatId;
            } else if (cat.catLvl === 1) {
                selectedL1.value = cat.catId;
            }
        }
    } else {
        selectedL1.value = null;
        selectedL2.value = null;
        selectedL3.value = null;
    }

    isEditing.value = true;
};

const cancelEdit = () => {
    isEditing.value = false;
};

const saveEdit = () => {
    if (!editTable.tableName || !editTable.schemaDef) {
        alert('테이블명과 스키마를 모두 입력해주세요.');
        return;
    }

    axios.post('/rag/database/table/save', {
        tableName: editTable.tableName,
        schemaDef: editTable.schemaDef,
        tableDesc: editTable.tableDesc,
        catId: selectedL3.value || selectedL2.value || selectedL1.value,
        sysId: selectedTable.value.sysId // Keep existing system
    }, {
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => {
            if (res.data.status === 'success') {
                alert('수정되었습니다.');
                fetchTables();
                isEditing.value = false;
            } else {
                alert('수정에 실패했습니다.');
            }
        })
        .catch(err => {
            console.error(err);
            alert('오류가 발생했습니다.');
        });
};

const saveCurrentSchema = () => {
    if (!compareTable.value.tableName || !compareTable.value.originalSchema) {
        alert('저장할 스키마 내용이 없습니다.');
        return;
    }

    saveLoading.value = true;
    axios.post('/rag/database/table/save', {
        tableName: compareTable.value.tableName,
        schemaDef: compareTable.value.originalSchema,
        tableDesc: compareTable.value.originalTableDesc,
        sysId: compareTable.value.sysId // Pass system
    }, {
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => {
            if (res.data.status === 'success') {
                alert('현재 스키마 내용이 저장되었습니다.');
                fetchTables();
                compareDialog.value = false;
            } else {
                alert('저장에 실패했습니다.');
            }
        })
        .catch(err => {
            console.error(err);
            alert('오류가 발생했습니다.');
        })
        .finally(() => {
            saveLoading.value = false;
        });
};

const fetchImportTables = () => {
    importLoading.value = true;
    axios.post('/rag/database/table/import/list', { sysId: importSystem.value })
        .then(res => {
            if (res.data.status === 'success') {
                const rawList = res.data.data.list;
                const existingMap = new Map(tables.value.map(t => [`${t.tableName}:${t.sysId}`, t]));

                importTables.value = rawList.map(item => {
                    const existingKey = `${item.tableName}:${importSystem.value}`;
                    const existing = existingMap.get(existingKey);
                    let status = 'NEW';
                    let originalSchema = '';
                    let originalTableDesc = '';

                    if (existing) {
                        status = 'EXISTS';
                        originalSchema = existing.schemaDef;
                        originalTableDesc = existing.tableDesc;
                    } else {
                        originalTableDesc = item.tableDesc || '';
                    }

                    return {
                        tableName: item.tableName,
                        schemaDef: item.schemaDef,
                        status: status,
                        originalSchema: originalSchema,
                        originalTableDesc: originalTableDesc,
                        introspectTableDesc: item.tableDesc,
                        sysId: importSystem.value
                    };
                });
            }
        })
        .catch(err => {
            console.error('Failed to fetch DB tables:', err);
        })
        .finally(() => {
            importLoading.value = false;
        });
};

const saveSelectedTables = async () => {
    if (selectedImportTables.value.length === 0) return;

    saveLoading.value = true;
    const tableMap = new Map(importTables.value.map(t => [t.tableName, t]));
    
    const payloadList = [];
    for (const tableName of selectedImportTables.value) {
        const table = tableMap.get(tableName);
        if (!table) continue;

        payloadList.push({
            tableName: table.tableName,
            schemaDef: table.schemaDef,
            tableDesc: table.introspectTableDesc,
            sysId: table.sysId
        });
    }

    if (payloadList.length === 0) {
        saveLoading.value = false;
        return;
    }

    try {
        const res = await axios.post('/rag/database/table/save-list', payloadList, {
            headers: { 'Content-Type': 'application/json' }
        });

        if (res.data.status === 'success') {
            alert(`${payloadList.length}개의 테이블 병렬 처리가 요청되었습니다.`);
        } else {
            alert('일부 테이블 저장에 실패했습니다.');
        }
    } catch (error) {
        console.error('Error batch saving tables', error);
        alert('테이블 다중 저장 중 오류가 발생했습니다.');
    }

    saveLoading.value = false;
    fetchTables();
    closeImportDialog();
};

const openCompareDialog = (importTable) => {
    compareTable.value = importTable;
    isCompareEditing.value = false; // Reset edit mode

    // Ensure the schema is formatted for better editing experience
    if (importTable.originalSchema) {
        importTable.originalSchema = formatSchema(importTable.originalSchema);
    }

    calculateAndSetDiff();
    compareDialog.value = true;
};

const toggleCompareEdit = () => {
    if (isCompareEditing.value) {
        // Just finished editing, re-calculate diff
        calculateAndSetDiff();
    }
    isCompareEditing.value = !isCompareEditing.value;
};

const calculateAndSetDiff = () => {
    const oldText = formatSchema(compareTable.value.originalSchema || '');
    const newText = formatSchema(compareTable.value.schemaDef || '');

    diffLines.value = calculateDiff(oldText, newText);
};

const confirmUpdateFromCompare = () => {
    // Add to selection if not already selected
    const isSelected = selectedImportTables.value.includes(compareTable.value.tableName);
    if (!isSelected) {
        selectedImportTables.value.push(compareTable.value.tableName);
    }
    compareDialog.value = false;
};

// Simple Diff Algorithm with Trim
function calculateDiff(oldText, newText) {
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');
    const result = [];

    let i = 0;
    let j = 0;

    while (i < oldLines.length || j < newLines.length) {
        const left = i < oldLines.length ? oldLines[i] : null;
        const right = j < newLines.length ? newLines[j] : null;

        // Use trim for comparison
        const leftTrim = left != null ? left.trim() : null;
        const rightTrim = right != null ? right.trim() : null;

        // Case 1: Matching
        if (leftTrim !== null && rightTrim !== null && leftTrim === rightTrim) {
            result.push({ leftContent: left, leftNum: i + 1, leftType: 'same', rightContent: right, rightNum: j + 1, rightType: 'same' });
            i++;
            j++;
        }
        // Case 2: Deletion
        // Heuristic: If right side has this line later?
        else if (leftTrim !== null && !findInNext(newLines, j, leftTrim)) {
            result.push({ leftContent: left, leftNum: i + 1, leftType: 'delete', rightContent: '', rightNum: '', rightType: 'empty' });
            i++;
        }
        // Case 3: Insertion
        else if (rightTrim !== null && !findInNext(oldLines, i, rightTrim)) {
            result.push({ leftContent: '', leftNum: '', leftType: 'empty', rightContent: right, rightNum: j + 1, rightType: 'insert' });
            j++;
        }
        // Case 4: Mismatch fallback
        else {
            // Basic naive fallback
            if (leftTrim !== null) {
                result.push({ leftContent: left, leftNum: i + 1, leftType: 'delete', rightContent: '', rightNum: '', rightType: 'empty' });
                i++;
            }
            if (rightTrim !== null) {
                result.push({ leftContent: '', leftNum: '', leftType: 'empty', rightContent: right, rightNum: j + 1, rightType: 'insert' });
                j++;
            }
        }
    }
    return result;
}

// Helper for lookahead
function findInNext(lines, startIndex, targetTrim) {
    for (let k = startIndex; k < lines.length; k++) {
        if (lines[k].trim() === targetTrim) return true;
    }
    return false;
}

const getDiffClass = (line, side) => {
    const type = side === 'left' ? line.leftType : line.rightType;

    if (type === 'delete') return 'bg-red-lighten-5 text-red-darken-4 diff-delete';
    if (type === 'insert') return 'bg-green-lighten-5 text-green-darken-4 diff-insert';
    // Optional: Add visual connector if needed
    return '';
};


const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
    }).format(new Date(date));
};

const formatSchema = (schema) => {
    if (!schema) return '';
    let formatted = schema;

    // Idempotent formatting
    // 1. Table opening: " (" -> " (\n    "
    if (formatted.includes(' (') && !formatted.includes(' (\n')) {
        formatted = formatted.replace(' (', ' (\n    ');
    }

    // 2. Columns commas: ", " -> ",\n    "
    // Use regex with negative lookahead to prevent double newlines
    formatted = formatted.replace(/, (?!\n)/g, ',\n    ');

    // 3. Closing: ");" -> "\n);"
    if (formatted.includes(');') && !formatted.includes('\n);')) {
        formatted = formatted.replace(');', '\n);');
    }

    return formatted;
};

const deleteTable = () => {
    if (!confirm('정말 삭제하시겠습니까?')) return;

    axios.post('/rag/database/table/delete', {
        tableName: selectedTable.value.tableName
    }, {
        headers: { 'Content-Type': 'application/json' }
    })
        .then(res => {
            if (res.data.status === 'success') {
                alert('삭제되었습니다.');
                viewDialog.value = false;
                fetchTables();
            } else {
                alert('삭제에 실패했습니다.');
            }
        })
        .catch(err => {
            console.error(err);
            alert('오류가 발생했습니다.');
        });
};

const fetchTables = () => {
    loading.value = true;
    const params = {
        schemaDef: search.value,
        sysId: systemFilter.value === 'all' ? null : systemFilter.value
    };
    axios.post('/rag/database/table/list', params)
        .then(res => {
            tables.value = res.data.data.list.map(item => ({
                ...item,
                addedTime: item.crtDt
            }));
        })
        .catch(err => {
            console.error('Failed to fetch tables:', err);
        })
        .finally(() => {
            loading.value = false;
        });
};

onMounted(() => {
    fetchSystemInfo();
    fetchTables();
    fetchCategories();
});
</script>

<style scoped>
.table-link {
    transition: color 0.2s;
}

.table-link:hover {
    color: #1a237e !important;
    text-decoration: underline !important;
}

.font-monospace {
    font-family: 'Consolas', 'Monaco', monospace !important;
}

.shadow-soft {
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.05) !important;
}

.modern-table :deep(thead tr th) {
    background-color: #283593 !important;
    color: #FFFFFF !important;
    font-weight: 700 !important;
    font-size: 0.85rem !important;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    height: 56px !important;
}

.modern-table :deep(tbody tr:nth-of-type(2n)) {
    background-color: #F0F2F5 !important;
}

.modern-table :deep(tbody tr:hover) {
    background-color: #E3F2FD !important;
}

.modern-table :deep(tbody tr td) {
    padding-top: 16px !important;
    padding-bottom: 16px !important;
    border-bottom: none !important;
}

.search-field :deep(.v-field__outline__start) {
    border-radius: 12px 0 0 12px !important;
}

.search-field :deep(.v-field__outline__end) {
    border-radius: 0 12px 12px 0 !important;
}

.select-none {
    user-select: none;
}

.custom-scroll::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.custom-scroll::-webkit-scrollbar-track {
    background: #f1f1f1;
}

.custom-scroll::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
}

.custom-scroll::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}

.line-row {
    line-height: 1.5;
}

.line-row:hover {
    background-color: rgba(0, 0, 0, 0.02);
}

.diff-delete {
    background-color: #ffeef0 !important;
}

.diff-insert {
    background-color: #e6ffed !important;
}

.code-text {
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 0.9em;
}
</style>
